import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiServices from "../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faVideo,
  faBookOpen,
  faEye,
  faStar,
  faClock,
  faMoneyBill,
  faPercent,
  faLayerGroup,
  faBook,
  faCalculator,
  faUser,
  faSpinner,
  faCertificate,
  faEyeSlash,
  faDesktop,
  faGraduationCap,
  faHardDrive,
} from "@fortawesome/free-solid-svg-icons";
import { toastHelper } from "../../Helpers/toastHelper";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import Loader from "../Shared/Loader";
import CourseRequest from "../Teacher/CourseManager/CourseRequest";
import AdminCourseSubscription from "./AdminCourseSubscription";
import Pagination from "../Shared/Pagination";
import { motion, AnimatePresence } from "framer-motion";
import DeleteAlert from "../Shared/DeleteAlert";
import extractErrorMessage from "../../Helpers/APIHelper";
import DynamicModal from "../Shared/DynamicModal";
import { useLanguage } from "../../contexts/LanguageContext";
import SoftGrid from "../Shared/SoftGrid/SoftGrid";
import ImagePreview from "../Shared/ImagePreview/ImagePreview";
import ComponentPopup from "../Shared/ComponentPopup";
import { encryptId } from "../../utils/encryption";

const PlatformCourses = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("courses");
  const [totalCapacity, setTotalCapacity] = useState(null);

  // Add pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [recalculatingId, setRecalculatingId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);
  const [editingCourse, setEditingCourse] = useState(null);

  const isEditing = Boolean(editingCourseId);

  // Live preview states
  const [previewCourseId, setPreviewCourseId] = useState(null);
  const [previewType, setPreviewType] = useState(null); // 'website' or 'student'

  // Update fetchCourses to handle pagination
  const fetchCourses = async (page = 1) => {
    setLoading(true);
    try {
      const res = await ApiServices.get(
        `CoursePlatform/getAll?PageSize=${pageSize}&PageNumber=${page}&IsActive=true`
      );
      setCourses(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setCurrentPage(res.data.current_page || 1);
      const cap = res?.data?.totalUserCoursesCapacityInMB ?? res?.data?.total_user_courses_capacity_in_mb ?? res?.data?.TotalUserCoursesCapacityInMB ?? res?.data?.items?.[0]?.totalUserCoursesCapacityInMB ?? res?.data?.items?.[0]?.total_user_courses_capacity_in_mb ?? res?.data?.items?.[0]?.TotalUserCoursesCapacityInMB;
      if (cap !== undefined && cap !== null) {
        setTotalCapacity(cap);
      }
    } catch (e) {
      toastHelper.error(t('failedToLoadCourses') || "تعذر تحميل الدورات");
    }
    setLoading(false);
  };

  const formatCapacity = (val) => {
    if (val === null || val === undefined) return null;
    const num = Number(val);
    if (isNaN(num)) return null;
    if (num >= 1024) {
      return `${parseFloat((num / 1024).toFixed(2))} GB`;
    }
    return `${parseFloat(num.toFixed(2))} MB`;
  };

  const capacityFormatted = formatCapacity(totalCapacity ?? courses?.[0]?.totalUserCoursesCapacityInMB ?? courses?.[0]?.total_user_courses_capacity_in_mb ?? courses?.[0]?.TotalUserCoursesCapacityInMB);

  // Add pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCourses(newPage);
    }
  };

  // Modify useEffect to fetch courses
  useEffect(() => {
    fetchCourses();
  }, []);

  // Handle edit
  const handleEdit = async (courseId) => {
    try {
      const res = await ApiServices.get(`Course/${courseId}`);
      if (res?.data) {
        setEditingCourseId(courseId);
        setEditingCourse(res.data);
        setShowCourseModal(true);
      }
    } catch (error) {
      toastHelper.error(t('failedToLoadCourseDetails') || "تعذر تحميل بيانات الدورة");
    }
  };

  const handleAddCourse = () => {
    setEditingCourseId(null);
    setEditingCourse(null);
    setShowCourseModal(true);
  };

  // Handle delete
  const handleDelete = (course) => {
    setSelectedCourse(course);
    setShowDeleteAlert(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedCourse) return;

    setDeleteLoading(true);
    try {
      const response = await ApiServices.delete(
        `Course/delete/${selectedCourse.id}`
      );
      if (response.check) {
        toastHelper.success(t('courseDeletedSuccess') || "تم حذف الدورة بنجاح");
        fetchCourses(currentPage);
      }
    } catch (error) {
      toastHelper.error(t('courseDeleteFailed') || "حدث خطأ أثناء حذف الدورة");
    } finally {
      setDeleteLoading(false);
      setShowDeleteAlert(false);
      setSelectedCourse(null);
    }
  };

  const handleVideoClick = (courseId) => {
    navigate(`/teacher/courses/videos/${courseId}`);
  };

  const handleRecalculateHours = async (courseId) => {
    try {
      setRecalculatingId(courseId);
      const response = await ApiServices.put(
        `Course/ReCalculateCourseHour/${courseId}`
      );

      if (response.check) {
        toastHelper.success(t('courseHoursRecalculated') || "تم إعادة حساب ساعات الدورة بنجاح");
        fetchCourses(currentPage);
      } else {
        toastHelper.error(t('courseHoursRecalculateFailed') || "فشل في إعادة حساب ساعات الدورة");
      }
    } catch (error) {
      const title = error?.title || error?.message || "";
      if (title.toLowerCase().includes("validation errors")) {
        const errMessage = extractErrorMessage(error);
        toastHelper.error(errMessage);
      } else {
        toastHelper.error(
          error || error?.msg || t('courseHoursRecalculateError') || "حدث خطأ أثناء إعادة حساب ساعات الدورة"
        );
      }
    } finally {
      setRecalculatingId(null);
    }
  };

  const handleToggleVisibility = async (courseId) => {
    try {
      setTogglingId(courseId);
      const response = await ApiServices.put(`Course/showOrHide/${courseId}`);
      if (response.check) {
        toastHelper.success(t('courseVisibilityChanged') || "تم تغيير حالة ظهور الدورة بنجاح");
        fetchCourses(currentPage);
      } else {
        toastHelper.error(t('courseVisibilityChangeFailed') || "فشل في تغيير حالة ظهور الدورة");
      }
    } catch (error) {
      toastHelper.error(error?.message || t('courseVisibilityChangeError') || "حدث خطأ أثناء تغيير حالة ظهور الدورة");
    } finally {
      setTogglingId(null);
    }
  };

  const courseFormConfig = (courseData) => [
    {
      name: "Name",
      label: t('courseName') || "اسم الدورة",
      type: "text",
      required: true,
      requiredMessage: t('courseNameRequired') || "اسم الدورة مطلوب",
      min: 3,
      max: 100,
      defaultValue: courseData?.name || "",
    },
    {
      name: "Description",
      label: t('courseDescription') || "وصف الدورة",
      type: "styledText",
      required: true,
      requiredMessage: t('courseDescriptionRequired') || "وصف الدورة مطلوب",
      min: 10,
      max: 50000,
      defaultValue: courseData?.description || "",
    },
    {
      name: "Price",
      label: t('price') || "السعر",
      type: "number",
      required: true,
      requiredMessage: t('coursePriceRequired') || "السعر مطلوب",
      min: 0,
      defaultValue: courseData?.price ?? 0,
    },
    {
      name: "Discount",
      label: t('courseDiscountValue') || "قيمة الخصم",
      type: "number",
      min: 0,
      defaultValue: courseData?.discount ?? 0,
    },
    {
      name: "CategoryId",
      label: t('courseCategory') || "التصنيف",
      type: "async-select",
      required: true,
      requiredMessage: t('courseCategoryRequired') || "التصنيف مطلوب",
      optionsUrl: "Lookup/Categories",
      optionsMap: (item) => ({ value: String(item.id), label: item.name }),
      valueLabel: courseData?.category,
      defaultValue: courseData?.category_id ? String(courseData.category_id) : "",
    },
    {
      name: "MaterialId",
      label: t('examMaterial') || "المادة",
      type: "async-select",
      required: true,
      requiredMessage: t('examMaterialRequired') || "المادة مطلوبة",
      optionsUrl: "Lookup/Materials",
      optionsMap: (item) => ({ value: String(item.id), label: item.name }),
      valueLabel: courseData?.materail || courseData?.material,
      defaultValue: courseData?.material_id ? String(courseData.material_id) : "",
    },
    {
      name: "Coupon",
      label: t('courseCouponOptional') || "كود الكوبون (اختياري)",
      type: "text",
      defaultValue: courseData?.coupon || "",
    },
    {
      name: "IsCertified",
      label: t('courseIsCertified') || "هل الدورة معتمدة؟",
      type: "select",
      required: true,
      defaultValue: String(courseData?.is_certified ? "true" : "false"),
      options: [
        { value: "true", label: t('examResetYes') || "نعم" },
        { value: "false", label: t('examResetNo') || "لا" },
      ],
    },
    {
      name: "CourseCover",
      label: t('courseImage') || "صورة الدورة (اختياري)",
      type: "file",
      accept: "image/*",
    },
  ];

  const handleCourseModalSubmit = async (values) => {
    const formData = new FormData();
    formData.append("Name", values.Name ?? "");
    formData.append("Description", values.Description ?? "");
    formData.append("Price", values.Price ?? 0);
    formData.append("Discount", values.Discount ?? 0);
    formData.append("CategoryId", values.CategoryId ?? "");
    formData.append("MaterialId", values.MaterialId ?? "");
    formData.append("Coupon", values.Coupon ?? "");
    formData.append("IsCertified", values.IsCertified === "true");

    if (values.CourseCover instanceof File) {
      formData.append("CourseCover", values.CourseCover, values.CourseCover.name);
    }

    if (isEditing) {
      await ApiServices.put(`CoursePlatform/update/${editingCourseId}`, formData, true, 300000);
      toastHelper.success(t('courseUpdatedSuccess') || "تم تحديث الدورة بنجاح");
    } else {
      await ApiServices.post("CoursePlatform/create", formData, true, 300000);
      toastHelper.success(t('courseAddedSuccess') || "تم إضافة الدورة بنجاح");
    }

    setShowCourseModal(false);
    setEditingCourseId(null);
    setEditingCourse(null);
    fetchCourses(currentPage);
  };

  return (
    <div className="course-manager">
      <div className="section-header">
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div className="title-section">
            <h2>{t('courseManagementTitle') || "إدارة الدورات"}</h2>
            <span className="subtitle">{t('courseManagementSubtitle') || "إدارة وتنظيم بيانات الدورات"}</span>
          </div>
          {capacityFormatted && (
            <div
              className="capacity-label-badge"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 14px',
                borderRadius: '20px',
                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                color: '#4f46e5',
                fontWeight: '600',
                fontSize: '0.875rem',
                border: '1px solid rgba(99, 102, 241, 0.2)',
              }}
            >
              <FontAwesomeIcon icon={faHardDrive} />
              <span>
                {t('totalUserCoursesCapacityInMB') || (language === 'ar' ? 'إجمالي السعة' : 'Total Capacity')}: {capacityFormatted}
              </span>
            </div>
          )}
        </div>
        <div className="header-right">
          {activeTab === "courses" && (
            <button
              className="add-course-btn"
              onClick={handleAddCourse}
            >
              <FontAwesomeIcon icon={faPlus} />
              {t('addNewCourse') || "إضافة دورة جديدة"}
            </button>
          )}
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <motion.button
            className={`tab-btn ${activeTab === "courses" ? "active" : ""}`}
            onClick={() => setActiveTab("courses")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faBookOpen} /> {t('courses') || "الدورات"}
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "subscriptions" ? "active" : ""}`}
            onClick={() => setActiveTab("subscriptions")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faUser} /> {t('subscriptionsTab') || "الاشتراكات"}
          </motion.button>
          <motion.button
            className={`tab-btn ${activeTab === "requests" ? "active" : ""}`}
            onClick={() => setActiveTab("requests")}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FontAwesomeIcon icon={faUser} /> {t('registrationRequestsTab') || "طلبات التسجيل"}
          </motion.button>
        </div>
      </div>

      {activeTab === "courses" ? (
        <div className="courses-container" style={{ marginTop: '2rem' }}>
          <SoftGrid
            columns={[
              {
                key: "course_cover",
                label: t('courseImage') || "صورة الدورة",
                render: (value, row) => (
                  <ImagePreview
                    src={value ? getFullFileUrl(value) : null}
                    alt={row.name}
                    style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover' }}
                    fallbackIcon={<FontAwesomeIcon icon={faBookOpen} />}
                  />
                )
              },
              {
                key: "name",
                label: t('courseName') || "اسم الدورة",
                sortable: true,
                render: (value, row) => (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: language === 'ar' ? 'right' : 'left' }}>
                    <span style={{ fontWeight: 600 }}>{value}</span>
                    {row.is_certified && (
                      <span style={{ fontSize: '0.75rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <FontAwesomeIcon icon={faCertificate} /> {t('certified') || "معتمدة"}
                      </span>
                    )}
                  </div>
                )
              },
              { key: "category", label: t('courseCategory') || "التصنيف", sortable: true },
              { key: "material_name", label: t('examMaterial') || "المادة", sortable: true },
              {
                key: "price",
                label: t('price') || "السعر",
                sortable: true,
                render: (value) => `${value} ${t('currencyEGP') || "ج.م"}`
              },
              { key: "discount", label: t('courseDiscountValue') || "قيمة الخصم", sortable: true },
              {
                key: "views",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('views') || "المشاهدات"}</div>,
                sortable: true
              },
              {
                key: "rate",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('rate') || "التقييم"}</div>,
                sortable: true
              },
              {
                key: "hours",
                label: <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>{t('hours') || "الساعات"}</div>,
                sortable: true
              },
            ]}
            data={courses}
            isLoading={loading}
            isPaginated={true}
            pageNumber={currentPage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            emptyStateMessage={t('noCoursesYet') || "لا توجد دورات بعد"}
            actions={(row) => [
              {
                label: t('courseEdit') || "تعديل",
                icon: <FontAwesomeIcon icon={faEdit} />,
                onClick: () => handleEdit(row.id),
                className: 'edit'
              },
              {
                label: t('delete') || "حذف",
                icon: <FontAwesomeIcon icon={faTrash} />,
                onClick: () => handleDelete(row),
                className: 'delete'
              },
              {
                label: t('videos') || "الفيديوهات",
                icon: <FontAwesomeIcon icon={faVideo} />,
                onClick: () => handleVideoClick(row.id),
              },
              {
                label: row.is_hidden ? (t('showCourse') || "إظهار الدورة") : (t('hideCourse') || "إخفاء الدورة"),
                icon: togglingId === row.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={row.is_hidden ? faEyeSlash : faEye} />,
                onClick: () => handleToggleVisibility(row.id),
                disabled: togglingId === row.id,
                className: row.is_hidden ? 'warning' : ''
              },
              {
                label: t('recalculateHours') || "إعادة حساب الساعات",
                icon: recalculatingId === row.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faCalculator} />,
                onClick: () => handleRecalculateHours(row.id),
                disabled: recalculatingId === row.id
              },
              {
                label: t('websitePreview') || "معاينة الموقع",
                icon: <FontAwesomeIcon icon={faDesktop} />,
                onClick: () => { setPreviewCourseId(row.id); setPreviewType('website'); },
              }
            ]}
          />
        </div>
      ) : activeTab === "subscriptions" ? (
        <AdminCourseSubscription />
      ) : (
        <CourseRequest />
      )}

      <DeleteAlert
        isOpen={showDeleteAlert}
        onClose={() => {
          setShowDeleteAlert(false);
          setSelectedCourse(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('confirmDeleteCourseTitle') || "تأكيد حذف الدورة"}
        message={t('confirmDeleteCourseMessage') || "هل أنت متأكد من حذف هذه الدورة؟"}
        itemName={selectedCourse?.name}
        itemDescription={`${t('materialLabelPrefix') || "المادة: "}${selectedCourse?.materail || selectedCourse?.material_name || ""}`}
        loading={deleteLoading}
      />

      <DynamicModal
        isOpen={showCourseModal}
        onClose={() => {
          setShowCourseModal(false);
          setEditingCourseId(null);
          setEditingCourse(null);
        }}
        title={isEditing ? (t('courseEdit') || "تعديل الدورة") : (t('createNewCourseTitle') || "إنشاء دورة جديدة")}
        submitButtonText={isEditing ? (t('courseUpdate') || "تحديث الدورة") : (t('createCourseBtn') || "إنشاء الدورة")}
        formConfig={courseFormConfig(editingCourse)}
        multipart={true}
        direction={language === 'ar' ? 'rtl' : 'ltr'}
        onSubmit={handleCourseModalSubmit}
        onError={(error) => {
          const title = error?.title || error?.message || "";
          if (title.toLowerCase().includes("validation errors")) {
            const errMessage = extractErrorMessage(error);
            toastHelper.error(errMessage);
          } else {
            toastHelper.error(error || error?.msg || t('courseSaveError') || "حدث خطأ أثناء حفظ الدورة");
          }
        }}
      />

      {/* Website Live Preview */}
      <ComponentPopup
        isOpen={previewType === 'website' && previewCourseId !== null}
        onClose={() => { setPreviewCourseId(null); setPreviewType(null); }}
        title={t('websitePreview') || "معاينة الموقع"}
        subtitle={t('livePreviewSubtitle') || "معاينة مباشرة - للعرض فقط"}
        icon={faDesktop}
        enableLivePreview={true}
        maxWidth="95vw"
        maxHeight="95vh"
        previewUrl={`/course/${encryptId(previewCourseId) || previewCourseId}?preview=true`}
      />

      <style jsx>{`
        .course-manager {
          padding: 2rem;
          background: #fff;
          min-height: 100vh;
          border-radius: 30px;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 32px;
          padding: 24px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(8px);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .header-left {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .title-section {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .section-icon {
          font-size: 2rem;
          color: #6366f1;
        }

        h2 {
          font-size: 1.875rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .subtitle {
          color: #64748b;
          font-size: 1rem;
        }

        .add-course-btn {
          background: #6366f1;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          font-size: 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: "Zain", sans-serif;
        }

        .add-course-btn:hover {
          background: #4338ca;
        }

        .tabs-container {
          background: white;
          border-radius: 1rem;
          padding: 1rem;
          margin-bottom: 2rem;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        .tabs {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .tab-btn {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          color: #64748b;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          border-radius: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
          font-family: "Zain", sans-serif;
          white-space: nowrap;
        }

        .tab-btn:hover {
          background: #f1f5f9;
          color: #1e293b;
        }

        .tab-btn.active {
          background: #6366f1;
          color: white;
          font-family: "Zain", sans-serif;
        }

        .courses-container {
          margin-top: 2rem;
        }

        @media (max-width: 1024px) {
          .course-manager {
            padding: 1rem;
          }
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 1rem;
          }

          .add-course-btn {
            width: 100%;
            justify-content: center;
          }

          .tabs {
            flex-direction: column;
          }

          .tab-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default PlatformCourses;
